import { createFileRoute, stripSearchParams } from "@tanstack/react-router";
import { Suspense } from "react";
import { z } from "zod";

import { hasProjectPermission } from "@/app/capabilities/api/check-permission";
import { projectCapabilitiesQueryOptions } from "@/app/capabilities/api/get-capabilities";
import { PROJECT } from "@/app/capabilities/permissions";
import { hooksQueryOptions } from "@/app/logic-extensions/api/get-hooks";
import { pluginsQueryOptions } from "@/app/logic-extensions/api/get-plugins";
import { LogicExtensionsSkeleton } from "@/app/logic-extensions/components/logic-extensions-skeleton";
import { GroupAccessConfigPage } from "@/app/logic-extensions/pages/group-access-config-page";
import { PluginConfigPage } from "@/app/logic-extensions/pages/plugin-config-page";
import { RateLimitConfigPage } from "@/app/logic-extensions/pages/rate-limit-config-page";
import type { PluginType } from "@/app/logic-extensions/types";
import { requireProjectPermission } from "@/lib/route-guards";

const newPluginSearchSchema = z.object({
  type: z
    .enum(["webhook", "rate_limit", "group_access"])
    .catch("webhook")
    .default("webhook"),
});

const NewPluginPage = () => {
  const { type } = Route.useSearch();
  const navigate = Route.useNavigate();
  const handleSwitchType = (nextType: PluginType) => {
    navigate({ search: { type: nextType }, replace: true });
  };

  return (
    <Suspense fallback={<LogicExtensionsSkeleton />}>
      {type === "rate_limit" && (
        <RateLimitConfigPage mode="create" onSwitchType={handleSwitchType} />
      )}
      {type === "group_access" && (
        <GroupAccessConfigPage mode="create" onSwitchType={handleSwitchType} />
      )}
      {type === "webhook" && (
        <PluginConfigPage mode="create" onSwitchType={handleSwitchType} />
      )}
    </Suspense>
  );
};

export const Route = createFileRoute(
  "/_authenticated/orgs/$organizationId/projects/$projectId/contextual-access/new"
)({
  validateSearch: newPluginSearchSchema,
  search: {
    middlewares: [stripSearchParams({ type: "webhook" })],
  },
  component: NewPluginPage,
  beforeLoad: (ctx) => requireProjectPermission(ctx, PROJECT.plugins.create),
  loader: async ({ context: { queryClient }, params }) => {
    queryClient.prefetchQuery(
      pluginsQueryOptions({ binding: "project", scope: params })
    );
    // Prefetch hooks only when the caller can read them — the editor loads hooks
    // conditionally (hooks_read), so an unreadable prefetch would just 403.
    const capabilities = await queryClient.ensureQueryData(
      projectCapabilitiesQueryOptions(params)
    );
    if (hasProjectPermission(capabilities.authz, PROJECT.hooks.read)) {
      queryClient.prefetchQuery(
        hooksQueryOptions({ binding: "project", scope: params })
      );
    }
    return { crumb: "New System" };
  },
});
